import { useRef, useEffect } from "react";
import { ArrowUpRight, FileText, ScanLine, Scale, Rocket, Info, Percent } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { DeviceFrameDesktop, DeviceFramePhone } from "@/components/DeviceFrames";
import { useLang } from "@/i18n/translations";
import { NOVA_BEAT_ASSETS, PROJECTS } from "@/lib/siteConfig";
import { PinnedScreenSequence } from "@/components/PinnedScreenSequence";

// Phase 12 — cinematic polish: zoom focus points (in % of the screenshot)
// Wizard: subtle drifting zoom, no highlight (calm keynote feel)
const WIZARD_FOCUS = [
  { x: 50, y: 40, scale: 1.18 },
  { x: 50, y: 45, scale: 1.2 },
  { x: 42, y: 45, scale: 1.22 },
  { x: 45, y: 42, scale: 1.22 },
  { x: 55, y: 45, scale: 1.2 },
];
// PDF pages: zoom + spotlight highlight on the real section positions
// (coordinates verified against the rendered demo protocol pages)
const PDF_FOCUS = [
  { focus: { x: 50, y: 35, scale: 1.2 }, highlight: { x: 6, y: 22, w: 88, h: 27 } }, // Berechnung auf einen Blick + Risikoampel
  { focus: { x: 50, y: 66, scale: 1.22 }, highlight: { x: 6, y: 56, w: 88, h: 30 } }, // Rechtsgrundlage & Quelle
  { focus: { x: 50, y: 50, scale: 1.2 }, highlight: { x: 6, y: 31, w: 88, h: 39 } }, // NoVA-Berechnung + Rechenweg
  { focus: { x: 50, y: 27, scale: 1.22 }, highlight: { x: 6, y: 13, w: 88, h: 20 } }, // Bewertung & Risiko
];

const NOVA = PROJECTS.find((p) => p.id === "novapilot");
const TEAL = "#14b8a6"; // Clean-SaaS Tuerkis — CloudService-Cyan bleibt dominant

// Lazy autoplay video: loads + plays only when visible, pauses offscreen.
const LazyVideo = ({ src, webm, poster, width, height, testId }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          if (v.preload === "none") v.preload = "auto";
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      data-testid={testId}
      poster={poster}
      width={width}
      height={height}
      muted
      loop
      playsInline
      preload="none"
      className="block w-full h-auto"
    >
      <source src={src} type="video/mp4" />
      {webm && <source src={webm} type="video/webm" />}
    </video>
  );
};

const Kicker = ({ icon: Icon, children }) => (
  <p className="flex items-center gap-2 text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
    {Icon ? (
      <Icon className="w-3.5 h-3.5" style={{ color: TEAL }} />
    ) : (
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: TEAL }} aria-hidden="true" />
    )}
    {children}
  </p>
);

const Shot = ({ asset, eager = false }) => (
  <img
    src={asset.src}
    alt={asset.alt}
    width={asset.w}
    height={asset.h}
    loading={eager ? "eager" : "lazy"}
    className="block w-full h-auto"
  />
);

export const NovaPilotCaseStudy = () => {
  const { t } = useLang();

  return (
    <section
      id="case-novapilot"
      data-testid="case-study-novapilot"
      className="relative py-20 sm:py-28 bg-[#070a12] border-y border-white/5 overflow-x-clip"
    >
      <div
        aria-hidden="true"
        className="absolute top-[2%] left-[-12%] w-[560px] h-[560px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(20,184,166,0.07), transparent 70%)" }}
      />

      {/* ---- Header ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <Kicker>{t.nova.kicker}</Kicker>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.025em] leading-[1.08] text-foreground max-w-3xl">
            {t.nova.headline}
          </h1>
          <p className="mt-5 text-base lg:text-lg text-muted-foreground leading-relaxed max-w-2xl">{t.nova.intro}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {NOVA.allBadges.map((b) => (
              <Badge key={b} variant="outline" className="bg-white/5 border-white/10 text-foreground/80 font-mono-cs text-[10px] font-normal rounded-full px-2.5">
                {b}
              </Badge>
            ))}
          </div>
          <a
            data-testid="nova-visit-link"
            href={NOVA.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#00d4ff] hover:text-[#33ddff] transition-colors duration-200"
          >
            {t.nova.visit} <ArrowUpRight className="w-4 h-4" />
          </a>
        </Reveal>
      </div>

      {/* ---- Beat 1 — Problem ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-16" data-testid="nova-beat-1">
        <Reveal>
          <Kicker>{t.nova.b1Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nova.b1Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.nova.b1Text}</p>
        </Reveal>
        <Reveal delay={0.12} className="mt-9">
          <DeviceFrameDesktop url="novapilot.at" testId="nova-hero-frame">
            <Shot asset={NOVA_BEAT_ASSETS.heroDesktop} eager />
          </DeviceFrameDesktop>
        </Reveal>
        <Reveal delay={0.1} className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <DeviceFrameDesktop url="novapilot.at" testId="nova-fallassistent-frame">
              <Shot asset={NOVA_BEAT_ASSETS.fallassistentDesktop} />
            </DeviceFrameDesktop>
          </div>
          <p className="lg:col-span-4 text-sm text-muted-foreground leading-relaxed font-mono-cs">{t.nova.intro}</p>
        </Reveal>
      </div>

      {/* ---- Beat 2 — Gefuehrter Fall-Assistent ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="nova-beat-2">
        <Reveal>
          <Kicker icon={Rocket}>{t.nova.b2Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nova.b2Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-2xl">{t.nova.b2Text}</p>
        </Reveal>
        <Reveal delay={0.1} className="mt-9">
          <DeviceFrameDesktop url="novapilot.at/app" testId="nova-dashboard-frame">
            <Shot asset={NOVA_BEAT_ASSETS.dashboardDesktop} />
          </DeviceFrameDesktop>
        </Reveal>
        {/* Pinned wizard sequence (Phase 12) — 5 real wizard steps */}
        <div className="mt-14">
          <PinnedScreenSequence
            steps={t.nova.wizardSeq.map((s, i) => ({ ...s, media: NOVA_BEAT_ASSETS.wizardSeq[i], focus: WIZARD_FOCUS[i] }))}
            accent={TEAL}
            url="novapilot.at/app"
            aspect="1920/1098"
            testId="nova-wizard-seq"
          />
        </div>
      </div>

      {/* ---- Beat 3 — Dokumente & OCR ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="nova-beat-3">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-4">
            <Kicker icon={ScanLine}>{t.nova.b3Kicker}</Kicker>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nova.b3Title}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.nova.b3Text}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <DeviceFrameDesktop url="novapilot.at/app" testId="nova-upload-frame">
              <Shot asset={NOVA_BEAT_ASSETS.uploadDesktop} />
            </DeviceFrameDesktop>
          </Reveal>
        </div>
        <Reveal delay={0.12} className="mt-12 flex justify-center gap-6 sm:gap-10">
          <div className="w-[200px] sm:w-[240px] mt-10">
            <DeviceFramePhone testId="nova-docpicker-phone">
              <Shot asset={NOVA_BEAT_ASSETS.docpickerMobile} />
            </DeviceFramePhone>
          </div>
          <div className="w-[200px] sm:w-[240px]">
            <DeviceFramePhone testId="nova-doclist-phone">
              <Shot asset={NOVA_BEAT_ASSETS.doclistMobile} />
            </DeviceFramePhone>
          </div>
        </Reveal>
      </div>

      {/* ---- Beat 4 — Rule Engine & Rechtslagenlogik ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="nova-beat-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <Kicker icon={Scale}>{t.nova.b4Kicker}</Kicker>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nova.b4Title}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.nova.b4Text}</p>
            <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-6" data-testid="nova-rule-engine-box">
              <p className="text-sm font-medium text-foreground">{t.nova.ruleTitle}</p>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.nova.ruleText}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {t.nova.ruleChips.map((c) => (
                  <span
                    key={c}
                    className="px-2.5 py-1 rounded-full text-[10px] font-mono-cs uppercase tracking-[0.1em] border"
                    style={{ borderColor: "rgba(20,184,166,0.35)", color: TEAL, backgroundColor: "rgba(20,184,166,0.07)" }}
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <DeviceFrameDesktop url="novapilot.at" testId="nova-rechtslagen-frame">
              <Shot asset={NOVA_BEAT_ASSETS.rechtslagenDesktop} />
            </DeviceFrameDesktop>
          </Reveal>
        </div>
      </div>

      {/* ---- Beat 4b — USt- & Preislogik ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="nova-beat-4b">
        <Reveal>
          <Kicker icon={Percent}>{t.nova.b4bKicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nova.b4bTitle}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-2xl">{t.nova.b4bText}</p>
        </Reveal>
        <Reveal delay={0.1} className="mt-9">
          <DeviceFrameDesktop url="novapilot.at/app" testId="nova-ust-frame">
            <Shot asset={NOVA_BEAT_ASSETS.ustDesktop} />
          </DeviceFrameDesktop>
        </Reveal>
        <Reveal delay={0.1} className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <DeviceFrameDesktop url="novapilot.at/app" testId="nova-bewertung-frame">
              <Shot asset={NOVA_BEAT_ASSETS.bewertungDesktop} />
            </DeviceFrameDesktop>
          </div>
          <div className="lg:col-span-4 flex justify-center">
            <div className="w-[230px] sm:w-[260px]">
              <DeviceFramePhone testId="nova-bewertung-phone">
                <Shot asset={NOVA_BEAT_ASSETS.bewertungMobile} />
              </DeviceFramePhone>
            </div>
          </div>
        </Reveal>
      </div>

      {/* ---- Beat 5 — PDF-Berechnungsprotokoll (echtes Demo-PDF) ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="nova-beat-5">
        <Reveal>
          <Kicker icon={FileText}>{t.nova.b5Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground max-w-2xl">{t.nova.b5Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-2xl">{t.nova.b5Text}</p>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="nova-pdf-contents">
            {t.nova.b5Chips.map((c) => (
              <span
                key={c}
                className="px-2.5 py-1 rounded-full text-[10px] font-mono-cs uppercase tracking-[0.1em] border"
                style={{ borderColor: "rgba(20,184,166,0.35)", color: TEAL, backgroundColor: "rgba(20,184,166,0.07)" }}
              >
                {c}
              </span>
            ))}
          </div>
        </Reveal>
        {/* Pinned PDF sequence (Phase 12) — zoom + spotlight on the real protocol sections */}
        <div className="mt-12">
          <PinnedScreenSequence
            steps={t.nova.pdfSeq.map((s, i) => ({
              ...s,
              media: NOVA_BEAT_ASSETS.pdfPages[i],
              focus: PDF_FOCUS[i].focus,
              highlight: PDF_FOCUS[i].highlight,
            }))}
            accent={TEAL}
            frame="document"
            aspect="1200/1698"
            stackedFallback={false}
            testId="nova-pdf-seq"
          />
        </div>
        <Reveal delay={0.1} className="mt-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {NOVA_BEAT_ASSETS.pdfPages.map((p, i) => (
              <a
                key={p.src}
                data-testid={`nova-pdf-page-${i + 1}`}
                href={NOVA_BEAT_ASSETS.pdfFile}
                target="_blank"
                rel="noopener noreferrer"
                className="block overflow-hidden rounded-lg border border-white/15 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.35)] hover:border-[#14b8a6]/60 transition-[border-color,transform] duration-200 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Shot asset={p} />
              </a>
            ))}
          </div>
          <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
            <a
              data-testid="nova-pdf-button"
              href={NOVA_BEAT_ASSETS.pdfFile}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 self-start rounded-full border px-5 h-10 text-xs font-mono-cs uppercase tracking-[0.12em] transition-[background-color,border-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{ borderColor: "rgba(20,184,166,0.4)", color: TEAL, backgroundColor: "rgba(20,184,166,0.06)" }}
            >
              <FileText className="w-3.5 h-3.5" />
              {t.nova.b5PdfButton}
            </a>
            <p className="text-xs font-mono-cs text-muted-foreground/60 leading-relaxed" data-testid="nova-pdf-note">
              {t.nova.b5PdfNote}
            </p>
          </div>
        </Reveal>
      </div>

      {/* ---- Beat 6 — Lead-Funnel / Pro-Modell / Mobile-first ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="nova-beat-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <Kicker>{t.nova.b6Kicker}</Kicker>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nova.b6Title}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.nova.b6Text}</p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="flex justify-center gap-6 sm:gap-10">
              <div className="w-[200px] sm:w-[240px] mt-10">
                <DeviceFramePhone testId="nova-dashboard-phone">
                  <Shot asset={NOVA_BEAT_ASSETS.dashboardMobile} />
                </DeviceFramePhone>
              </div>
              <div className="w-[200px] sm:w-[240px]">
                <DeviceFramePhone testId="nova-pro-phone">
                  <Shot asset={NOVA_BEAT_ASSETS.proMobile} />
                </DeviceFramePhone>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mt-12">
          <DeviceFrameDesktop url="novapilot.at" testId="nova-pro-frame">
            <Shot asset={NOVA_BEAT_ASSETS.proDesktop} />
          </DeviceFrameDesktop>
        </Reveal>
      </div>

      {/* ---- Live Flow ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="nova-beat-flow">
        <Reveal>
          <Kicker>{t.nova.flowKicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.nova.flowTitle}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.nova.flowText}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal delay={0.08} className="lg:col-span-8">
            <DeviceFrameDesktop url="novapilot.at" testId="nova-flow-desktop-frame">
              <LazyVideo
                src={NOVA_BEAT_ASSETS.flowDesktop.src}
                webm={NOVA_BEAT_ASSETS.flowDesktop.webm}
                poster={NOVA_BEAT_ASSETS.flowDesktop.poster}
                width={NOVA_BEAT_ASSETS.flowDesktop.w}
                height={NOVA_BEAT_ASSETS.flowDesktop.h}
                testId="nova-flow-desktop-video"
              />
            </DeviceFrameDesktop>
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-4 flex justify-center">
            <div className="w-[230px] sm:w-[260px]">
              <DeviceFramePhone testId="nova-flow-phone-frame">
                <LazyVideo
                  src={NOVA_BEAT_ASSETS.flowMobile.src}
                  webm={NOVA_BEAT_ASSETS.flowMobile.webm}
                  poster={NOVA_BEAT_ASSETS.flowMobile.poster}
                  width={NOVA_BEAT_ASSETS.flowMobile.w}
                  height={NOVA_BEAT_ASSETS.flowMobile.h}
                  testId="nova-flow-mobile-video"
                />
              </DeviceFramePhone>
            </div>
          </Reveal>
        </div>
      </div>

      {/* ---- Rechtlicher Hinweis ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-20">
        <Reveal>
          <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="nova-legal-note">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground/70" />
            <p className="text-xs font-mono-cs text-muted-foreground/70 leading-relaxed">{t.nova.legalNote}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
